/**
 * AutomationPage - Phase 5 Enhanced Automation Management
 * Advanced workflow automation with integrated Phase 4 components
 * 
 * @description Professional automation interface combining:
 * - Workflow automation management with visual builder
 * - Real-time execution monitoring and analytics
 * - Multi-cloud automation orchestration
 * - Cost optimization and performance automation
 * - Security and compliance automation workflows
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCloudProviderStore } from "@/stores/cloudProviderStore";
import { 
  getLocalProviderFilterOptions,
  getFilterSummary 
} from "@/utils/providerFiltering";
import { 
  Play, 
  Pause, 
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  DollarSign,
  Activity,
  TrendingUp,
  Workflow,
  Timer,
  BarChart3,
  Shield,
  Cloud,
  Server,
  Database,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Eye
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Enhanced automation interfaces
interface AutomationWorkflow {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  lastRun: string;
  status: 'active' | 'completed' | 'failed' | 'paused' | 'scheduled';
  provider: 'aws' | 'gcp' | 'azure' | 'multi-cloud';
  category: 'performance' | 'backup' | 'cost' | 'security' | 'compliance';
  executionCount: number;
  successRate: number;
  avgDuration: number;
  nextRun?: string;
  costSavings?: number;
  tags: string[];
}

interface AutomationMetrics {
  totalWorkflows: number;
  activeWorkflows: number;
  executionsToday: number;
  successRate: number;
  costSavings: number;
  timesSaved: number;
}

interface ExecutionHistory {
  id: string;
  workflowId: string;
  workflowName: string;
  startTime: string;
  endTime: string;
  status: 'success' | 'failed' | 'running';
  duration: number;
  resourcesAffected: number;
  errorMessage?: string;
}

// Mock data for enhanced automation features
const mockWorkflows: AutomationWorkflow[] = [
  {
    id: 'wf-001',
    name: 'Intelligent Auto-Scaling',
    description: 'ML-powered scaling based on traffic patterns and resource utilization',
    enabled: true,
    lastRun: '2 hours ago',
    status: 'active',
    provider: 'aws',
    category: 'performance',
    executionCount: 156,
    successRate: 98.7,
    avgDuration: 45,
    nextRun: 'in 30 minutes',
    costSavings: 2840,
    tags: ['ml', 'auto-scaling', 'performance']
  },
  {
    id: 'wf-002',
    name: 'Multi-Cloud Backup Orchestration',
    description: 'Automated backup across AWS, GCP, and Azure with encryption',
    enabled: true,
    lastRun: '6 hours ago',
    status: 'completed',
    provider: 'multi-cloud',
    category: 'backup',
    executionCount: 89,
    successRate: 100,
    avgDuration: 120,
    nextRun: 'tomorrow at 2:00 AM',
    tags: ['backup', 'encryption', 'multi-cloud']
  },
  {
    id: 'wf-003',
    name: 'Smart Cost Optimization',
    description: 'AI-driven cost optimization with resource rightsizing',
    enabled: false,
    lastRun: '1 day ago',
    status: 'paused',
    provider: 'gcp',
    category: 'cost',
    executionCount: 23,
    successRate: 95.6,
    avgDuration: 180,
    costSavings: 1520,
    tags: ['cost-optimization', 'ai', 'rightsizing']
  },
  {
    id: 'wf-004',
    name: 'Security Compliance Scanner',
    description: 'Automated security scanning and compliance reporting',
    enabled: true,
    lastRun: '4 hours ago',
    status: 'active',
    provider: 'azure',
    category: 'security',
    executionCount: 67,
    successRate: 92.5,
    avgDuration: 90,
    nextRun: 'in 2 hours',
    tags: ['security', 'compliance', 'scanning']
  },
  {
    id: 'wf-005',
    name: 'Database Maintenance Automation',
    description: 'Automated database optimization, cleanup, and health checks',
    enabled: true,
    lastRun: '8 hours ago',
    status: 'completed',
    provider: 'aws',
    category: 'performance',
    executionCount: 134,
    successRate: 96.3,
    avgDuration: 75,
    nextRun: 'daily at 3:00 AM',
    tags: ['database', 'maintenance', 'optimization']
  }
];

const mockMetrics: AutomationMetrics = {
  totalWorkflows: 5,
  activeWorkflows: 4,
  executionsToday: 23,
  successRate: 96.4,
  costSavings: 4360,
  timesSaved: 48
};

const mockExecutionHistory: ExecutionHistory[] = [
  {
    id: 'exec-001',
    workflowId: 'wf-001',
    workflowName: 'Intelligent Auto-Scaling',
    startTime: '2024-12-19T10:30:00Z',
    endTime: '2024-12-19T10:31:15Z',
    status: 'success',
    duration: 75,
    resourcesAffected: 8
  },
  {
    id: 'exec-002',
    workflowId: 'wf-004',
    workflowName: 'Security Compliance Scanner',
    startTime: '2024-12-19T09:15:00Z',
    endTime: '2024-12-19T09:16:30Z',
    status: 'success',
    duration: 90,
    resourcesAffected: 15
  },
  {
    id: 'exec-003',
    workflowId: 'wf-002',
    workflowName: 'Multi-Cloud Backup Orchestration',
    startTime: '2024-12-19T02:00:00Z',
    endTime: '2024-12-19T02:02:10Z',
    status: 'success',
    duration: 130,
    resourcesAffected: 12
  }
];

// Chart data for automation analytics
const executionTrendData = [
  { time: '00:00', executions: 12, success: 11 },
  { time: '04:00', executions: 18, success: 17 },
  { time: '08:00', executions: 25, success: 24 },
  { time: '12:00', executions: 30, success: 29 },
  { time: '16:00', executions: 22, success: 21 },
  { time: '20:00', executions: 15, success: 14 }
];

const categoryData = [
  { name: 'Performance', value: 35, color: '#3b82f6' },
  { name: 'Cost', value: 25, color: '#10b981' },
  { name: 'Security', value: 20, color: '#f59e0b' },
  { name: 'Backup', value: 15, color: '#8b5cf6' },
  { name: 'Compliance', value: 5, color: '#ef4444' }
];

export default function AutomationPage() {
  // Global provider state
  const globalProvider = useCloudProviderStore(state => state.provider);
  
  const [workflows] = useState<AutomationWorkflow[]>(mockWorkflows);
  const [filteredWorkflows, setFilteredWorkflows] = useState<AutomationWorkflow[]>(mockWorkflows);
  const [metrics] = useState<AutomationMetrics>(mockMetrics);
  const [executionHistory] = useState<ExecutionHistory[]>(mockExecutionHistory);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Filter workflows based on selected criteria
  useEffect(() => {
    let filtered = workflows;

    // First filter by global provider
    filtered = filtered.filter(wf => wf.provider === globalProvider);

    if (selectedCategory !== "all") {
      filtered = filtered.filter(wf => wf.category === selectedCategory);
    }

    if (providerFilter !== "all") {
      filtered = filtered.filter(wf => wf.provider === providerFilter);
    }

    if (selectedStatus !== "all") {
      filtered = filtered.filter(wf => wf.status === selectedStatus);
    }

    if (searchTerm) {
      filtered = filtered.filter(wf => 
        wf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wf.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wf.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredWorkflows(filtered);
  }, [workflows, selectedCategory, providerFilter, selectedStatus, searchTerm, globalProvider]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Activity className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <AlertCircle className="w-4 h-4" />;
      case 'paused': return <Pause className="w-4 h-4" />;
      case 'scheduled': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'performance': return <TrendingUp className="w-4 h-4" />;
      case 'backup': return <Database className="w-4 h-4" />;
      case 'cost': return <DollarSign className="w-4 h-4" />;
      case 'security': return <Shield className="w-4 h-4" />;
      case 'compliance': return <CheckCircle className="w-4 h-4" />;
      default: return <Workflow className="w-4 h-4" />;
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'aws': return <Cloud className="w-4 h-4 text-orange-500" />;
      case 'gcp': return <Cloud className="w-4 h-4 text-blue-500" />;
      case 'azure': return <Cloud className="w-4 h-4 text-blue-600" />;
      case 'multi-cloud': return <Server className="w-4 h-4 text-purple-500" />;
      default: return <Cloud className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Automation</h2>
        <div className="flex items-center space-x-2">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Create Workflow
          </Button>
        </div>
      </div>

      {/* Metrics Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Workflows</CardTitle>
            <Workflow className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalWorkflows}</div>
            <p className="text-xs text-muted-foreground">+2 from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <Activity className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeWorkflows}</div>
            <p className="text-xs text-muted-foreground">80% of total</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Executions Today</CardTitle>
            <BarChart3 className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.executionsToday}</div>
            <p className="text-xs text-muted-foreground">+15% from yesterday</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.successRate}%</div>
            <p className="text-xs text-muted-foreground">+2.1% improvement</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Savings</CardTitle>
            <DollarSign className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${metrics.costSavings}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time Saved</CardTitle>
            <Timer className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.timesSaved}h</div>
            <p className="text-xs text-muted-foreground">Manual work avoided</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="workflows" className="space-y-4">
        <TabsList>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="history">Execution History</TabsTrigger>
        </TabsList>

        <TabsContent value="workflows" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Workflow Management</CardTitle>
              <CardDescription>Manage and monitor your automation workflows</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
                <div className="flex-1">
                  <Label>Search Workflows</Label>
                  <Input
                    placeholder="Search by name, description, or tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="min-w-[150px]">
                  <Label>Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="backup">Backup</SelectItem>
                      <SelectItem value="cost">Cost</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="min-w-[150px]">
                  <Label>Provider</Label>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">
                      {getFilterSummary(globalProvider, providerFilter)}
                    </div>
                    <Select value={providerFilter} onValueChange={setProviderFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getLocalProviderFilterOptions(globalProvider).map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="min-w-[150px]">
                  <Label>Status</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workflows Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Success Rate</TableHead>
                    <TableHead>Last Run</TableHead>
                    <TableHead>Cost Savings</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkflows.map((workflow) => (
                    <TableRow key={workflow.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{workflow.name}</div>
                          <div className="text-sm text-muted-foreground">{workflow.description}</div>
                          <div className="flex space-x-1">
                            {workflow.tags.map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(workflow.status)}
                          <Badge className={getStatusColor(workflow.status)}>
                            {workflow.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getProviderIcon(workflow.provider)}
                          <span className="capitalize">{workflow.provider}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getCategoryIcon(workflow.category)}
                          <span className="capitalize">{workflow.category}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm font-medium">{workflow.successRate}%</div>
                          <Progress value={workflow.successRate} className="w-16" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm">{workflow.lastRun}</div>
                          {workflow.nextRun && (
                            <div className="text-xs text-muted-foreground">Next: {workflow.nextRun}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {workflow.costSavings ? (
                          <div className="text-sm font-medium text-green-600">
                            ${workflow.costSavings}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={workflow.enabled}
                            onCheckedChange={() => {}}
                            className="mr-2"
                          />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Workflow
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Play className="w-4 h-4 mr-2" />
                                Run Now
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Execution Trends</CardTitle>
                <CardDescription>Workflow executions over the last 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={executionTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="executions" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      name="Total Executions"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="success" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      name="Successful"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Workflow Categories</CardTitle>
                <CardDescription>Distribution by automation category</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Executions</CardTitle>
              <CardDescription>Latest workflow execution history</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Start Time</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Resources Affected</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executionHistory.map((execution) => (
                    <TableRow key={execution.id}>
                      <TableCell className="font-medium">{execution.workflowName}</TableCell>
                      <TableCell>{new Date(execution.startTime).toLocaleString()}</TableCell>
                      <TableCell>{execution.duration}s</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(execution.status)}>
                          {execution.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{execution.resourcesAffected}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4 mr-2" />
                          View Logs
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}